import React, { memo } from 'react';
import { isEmpty } from 'lodash';
import ForestCoverMonitorChart from './ForestCoverMonitorChart';
import MultiYearBarChart from './MultiYearBarChart';
import BarChartComponent from './BarChartComponent';
import MonitorComparisonMap from './MonitorComparisonMap';
import MultiYearMap from './MultiYearMap';
import CardSkeleton from 'components/common/skeleton/CardSkeleton';
import { Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import SelectiveChart from './SelectiveChart';
import SocChart from './MultiParamSeasonalChart';
import LineChartComponent from './LineChart';

const ChartComponent = memo(({ type, data }) => {
  switch (type) {
    case 'pie':
      return <ForestCoverMonitorChart data={data} />;
    case 'seasonal_bar':
      return <MultiYearBarChart data={data} />;
    case 'bar':
      return <BarChartComponent data={data} />;
    case 'line':
      return <LineChartComponent data={data} />;
    case 'line_bar':
      return <SocChart data={data} />;
    case 'line_bar_selective':
      return <SelectiveChart data={data} />;
    default:
      return null;
  }
});

ChartComponent.propTypes = {
  type: PropTypes.oneOf(['pie', 'seasonal_bar', 'bar', 'line']).isRequired,
  data: PropTypes.object.isRequired
};

const MapComponent = memo(
  ({ type, data, coordinates, legendData, keyId, info, variant, label }) => {
    switch (type) {
      case 'comparison':
        return (
          <MonitorComparisonMap
            key={label}
            landCoverData={data}
            coordinates={coordinates}
            keyId={keyId}
            legendData={legendData}
            variant={variant}
            info={info}
            label={label}
          />
        );
      case 'normal':
        return (
          <MultiYearMap
            key={label}
            data={data}
            coordinates={coordinates}
            legendData={legendData}
            info={info}
            variant={variant}
            label={label}
          />
        );
      default:
        return null;
    }
  }
);

MapComponent.propTypes = {
  type: PropTypes.oneOf(['comparison', 'normal']).isRequired,
  data: PropTypes.any.isRequired,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }), // Remove `.isRequired` if coordinates can be optional or might sometimes be undefined.
  legendData: PropTypes.any,
  keyId: PropTypes.string,
  info: PropTypes.any,
  variant: PropTypes.any,
  label: PropTypes.string
};

const ObservationRenderer = ({ observation, loading, coordinates }) => {
  if (loading) {
    return <CardSkeleton footer={false} />;
  }

  if (isEmpty(observation)) {
    return null;
  }

  return (
    <>
      {Object.entries(observation).map(([key, value]) => (
        <Col key={key} md={value?.width || 12}>
          {value.type === 'chart' && (
            <ChartComponent type={value.type_format} data={value} />
          )}
          {value.type === 'map' && (
            <MapComponent
              type={value.type_format}
              data={value.data}
              coordinates={coordinates}
              legendData={value.legend}
              keyId={key}
              info={value.description}
              variant={value.variant}
              label={value.label}
            />
          )}
        </Col>
      ))}
    </>
  );
};

ObservationRenderer.propTypes = {
  observation: PropTypes.objectOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['chart', 'map']).isRequired,
      type_format: PropTypes.string.isRequired,
      width: PropTypes.number,
      data: PropTypes.any,
      Legend: PropTypes.any
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }).isRequired
};

export default memo(ObservationRenderer);
